// eslint-disable-next-line no-unused-vars
import { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme } from "../hooks/useTheme.jsx";
import { motion, AnimatePresence, useAnimationControls } from 'motion/react';
import { ChevronLeft, Minus, Plus, Trash2, Loader2, User, UserCheck } from 'lucide-react';
import { ImageWrapper } from './ImageWrapper.jsx';
import { ProductDetailModal } from './ProductDetailModal.jsx';
import { getProductInfo } from '../utils/productHelpers.js';
import { getMaxPerItem } from '../hooks/useItems.js';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import Currency from '../utils/Currency.js';
import locationIcon from '../images/location.svg';

export function ShoppingCart({
  currency,
  locale,
  cart,
  cartItems,
  unavailableItemIds,
  commonTotal,
  normalTotal,
  enrichedItems,
  totalPromoDiscount,
  sellers,
  sellerColors,
  onUpdateQuantity,
  onUpdateItem,
  onRemoveItem,
  onClearCart,
  onBack,
  onCheckout,
  getExtras,
  isSearching,
  style,
  filteredStoreIds,
  storeId,
  getProductById,
  getProductModifiers,
  validProducts,
  drawerContainer,
  logActivity,
  loyaltyPrograms,
  identifiedPrograms,
  onOpenIdentification
}) {
  const { theme } = useTheme();
  const scanActionStyle = style?.global?.actionBar?.scan;
  // Mientras quede un producto sin disponibilidad el carro no avanza: el cliente tiene que sacarlo
  // a mano, para que el cambio sea algo que hizo él y no algo que la pantalla hizo sola.
  const hasUnavailableItems = (unavailableItemIds?.size || 0) > 0;

  // El botón de pagar no puede avanzar, así que al tocarlo sacude el aviso: en vez de no pasar nada
  // —que se lee como que la pantalla se colgó— el gesto apunta al motivo.
  const noticeShake = useAnimationControls();
  const shakeNotice = () => {
    noticeShake.start({
      x: [0, -12, 12, -9, 9, -5, 5, 0],
      transition: { duration: 0.45, ease: 'easeInOut' },
    });
  };
  const [editingItem, setEditingItem] = useState(null);

  const editingProduct = editingItem ? getProductById(editingItem.productId) : null;
  const editingModifiers = useMemo(() => {
    if (!editingProduct) return [];
    return getProductModifiers(editingProduct.modifiers || []);
  }, [editingProduct, getProductModifiers]);

  const handleEditItem = (item, product) => {
    logActivity('shopping-cart', 'edit-modifiers-button', 'click', true, {product_id: product.id, product_name: product.label || product.name || null});
    setEditingItem(item);
  };

  const handleSaveItem = (product, quantity, selectedExtras) => {
    if (!editingItem) return;
    if (onUpdateItem) onUpdateItem(editingItem.id, quantity, selectedExtras);
    setEditingItem(null);
  };

  const getSellerIndex = (sellerName) => {
    let index = sellers.findIndex(s => s === sellerName);
    if (index > sellerColors.length - 1) return index % sellerColors.length;
    return index !== -1 ? index : 0;
  };

  const handleClearCart = () => {
    logActivity('shopping-cart', 'clear-button', 'click', true, {cart_details: cart.details});
    onClearCart();
  };

  const handleQuantityIncrease = (item) => {
    const product = getProductById(item.productId);
    const oldQuantity = item.quantity;
    const maxPerItem = getMaxPerItem(product, storeId, filteredStoreIds);
    if (oldQuantity >= maxPerItem) return;
    const newQuantity = oldQuantity + 1;
    if (product) logActivity('shopping-cart', 'increase-quantity-button', 'click', true, {product_id: product.id, product_name: product.label || product.name || null, old_quantity: oldQuantity, new_quantity: newQuantity});
    onUpdateQuantity(item.id, newQuantity);
  };

  const handleQuantityDecrease = (item) => {
    const oldQuantity = item.quantity;
    const newQuantity = Math.max(1, oldQuantity - 1);
    const product = getProductById(item.productId);
    if (product) logActivity('shopping-cart', 'decrease-quantity-button', 'click', true, {product_id: product.id, product_name: product.label || product.name || null, old_quantity: oldQuantity, new_quantity: newQuantity});
    onUpdateQuantity(item.id, newQuantity);
  };

  const handleRemove = (item) => {
    const product = getProductById(item.productId);
    if (product) logActivity('shopping-cart', 'remove-item-button', 'click', true, {product_id: product.id, product_name: product.label || product.name || null});
    onRemoveItem(item.id);
  };

  const handleCheckout = () => {
    logActivity('shopping-cart', 'checkout-button', 'click', true, {cart_details: cart.details});
    onCheckout();
  };

  const handleBack = () => {
    logActivity('shopping-cart', 'back-button', 'click', true, {cart_details: cart.details});
    onBack();
  };

  const hasLoyaltyPrograms = loyaltyPrograms && loyaltyPrograms.length > 0;
  const isIdentified = identifiedPrograms && Object.keys(identifiedPrograms).length > 0;
  // Only one program can be identified at a time, so the greeting comes from whichever
  // one it is. The name is best-effort: some programs hand us no name at all. Only the
  // first name is used - the greeting shares its row with the title, and a full name
  // eats the space the title needs on smaller screens.
  const memberName = Object.values(identifiedPrograms || {})[0]?.membership?.status_snapshot?.name || null;
  const memberFirstName = memberName ? memberName.trim().split(/\s+/)[0] : null;

  const handleOpenIdentification = () => {
    logActivity('shopping-cart', 'identify-button', 'click', true, {});
    onOpenIdentification();
  };

  return (
    <div className={`min-h-(--app-h) bg-mid pb-[230px] pt-[125px] overflow-y-auto ${cartItems.length === 0 ? 'flex flex-col items-center justify-between' : ''}`}>

      {/* TÍTULO PRINCIPAL */}
      <div className="bg-mid fixed z-50 top-0 left-0 right-0 w-full h-[112px] pt-[20px] pb-[20px] flex justify-center items-center shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]">
        {/* Three columns rather than a centred title with the button floated over it:
            the two 1fr columns stay equal, so the title is centred whatever sits beside
            it, and the greeting shrinks instead of running into it on smaller screens. */}
        <div className="max-w-screen-xl w-full h-full mx-auto px-[40px] grid grid-cols-[1fr_auto_1fr] items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {hasLoyaltyPrograms && (
              <>
              {/* Same plain icon button as the header's home and theme controls, with its
                  caption underneath. */}
              <motion.button
                onClick={handleOpenIdentification}
                className="flex flex-col items-center justify-center w-16 shrink-0 cursor-pointer transition-colors"
                aria-label={isIdentified ? 'Mi cuenta' : 'Identifícate'}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.5 }}
              >
                {isIdentified ? (
                  <UserCheck className="w-10 h-10 text-green-600" style={{ strokeWidth: 2 }} />
                ) : (
                  <User className="w-10 h-10" style={{ strokeWidth: 2 }} />
                )}
                <span className="secondary-font-bold fs-12 lh-14 mt-1 whitespace-nowrap">
                  {isIdentified ? 'Mi cuenta' : 'Identifícate'}
                </span>
              </motion.button>
              {isIdentified && memberFirstName && (
                <span className="secondary-font-bold fs-18 lh-22 truncate">
                  Hola, {memberFirstName}
                </span>
              )}
              </>
            )}
          </div>
          {/* SOK-09: el título ya es grande; con "Letra grande" crece hasta 56px y no más, para no
              tapar el botón Identifícate de la columna izquierda. */}
          <h1 className="text-center main-font-bold font-bold" style={{ fontSize: 'min(calc(48px * var(--fs-scale, 1)), 56px)', lineHeight: 'min(calc(56px * var(--fs-scale, 1)), 64px)' }}>
            Carro de compras
          </h1>
          <div />
        </div>
      </div>

      {/* BOTÓN VACIAR CARRO */}
      <div className="max-w-screen-xl mx-auto px-[40px] pt-[20px] mb-[20px] flex w-[100%] justify-between">
        {cartItems && cartItems.length > 0 ? (
          <motion.button
            onClick={handleClearCart}
            disabled={cartItems.length === 0}
            className="cursor-pointer h-[60px] px-[28px] rounded-[40px] border border-[2px] border-[#E1E1E1] bg-mid shadow-[0_2px_8px_rgba(0,0,0,0.06)] flex items-center gap-2 disabled:opacity-50 transition-colors"
            whileTap={{ scale: 0.9 }}
            whileHover={{ scale: 1.1 }}
          >
            <Trash2 className="w-5 h-5 text-(--color-text-subtle)" />
            <span className="text-(--color-text-subtle) fs-16 secondary-font-bold">
              Vaciar carro
            </span>
          </motion.button>
        ) : null}

        {!cartItems || cartItems.length === 0 ? (
          <div className='w-[0px] h-[0px] opacity-0'></div>
        ) : null}

        {scanActionStyle && (isSearching ? (
          <div className="flex items-center justify-between min-w-[300px] border border-(--color-text-subtle) rounded p-[10px] border-dashed">
            <p className="font-semibold text-(--color-text-subtle)">
              Buscando producto...
            </p>
            <Loader2 className="animate-spin text-(--color-text-subtle) h-10" size={40} />
          </div>
        ) : (
          <div className="flex items-center justify-between min-w-[300px] border border-(--color-text-subtle) rounded pl-[25px] pt-[10px] pb-[10px] pr-[10px] border-dashed">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-(--color-text-subtle)">
                Escanea tus productos
              </span>
            </div>
            <div className="h-10 opacity-70 border-none outline-none shadow-none">
              <DotLottieReact src="lotties/barcodeAnimation.lottie" autoplay loop themeId={theme === "dark" ? "DarkMode" : "Default"} />
            </div>
          </div>
        ))}
      </div>

      {/* LISTADO DE PRODUCTOS */}
      <div className="max-w-screen-xl mx-auto px-[40px] space-y-[20px] pb-[180px]">
        {cartItems.length === 0 ? (
          <div className="text-center py-[60px]">
            <p className="text-(--color-text-subtle) fs-20 secondary-font-regular">
              Tu carrito está vacío
            </p>
          </div>
        ) : (
          enrichedItems.map((item, index) => {
            const product = getProductById(item.productId);
            if (!product) return null;
            const isUnavailable = unavailableItemIds?.has(item.id) || false;
            const productModifiers = getProductModifiers(product.modifiers || [])
            const { normalPrice, commonPrice, mainImage } = getProductInfo(product, filteredStoreIds);
            const extras = getExtras(product, productModifiers, item.selectedExtras);
            const extraSubtotal = extras.reduce((sum, ex) => sum += (ex.price * ex.quantity), 0)
            const itemNormalSubtotal = (normalPrice + extraSubtotal);
            const itemCommonSubtotal = (commonPrice + extraSubtotal);
            const itemNormalTotal = (normalPrice + extraSubtotal) * item.quantity;
            const itemCommonTotal = ((commonPrice + extraSubtotal) * item.quantity) - (item.promoDiscount || 0);
            const sellerIndex = getSellerIndex(product.seller_name);
            const sellerColor = sellerColors[sellerIndex];
            return (
              <AnimatePresence key={`${item.productId}-${index}`} mode="wait">
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`bg-back border border-[4px] rounded-[40px] p-[20px] shadow-[0_2px_12px_rgba(0,0,0,0.06)] ${isUnavailable ? 'border-[#D92D20]' : 'border-[#F2F2F2]'}`}
                >
                  {isUnavailable && (
                    <div
                      className="secondary-font-bold fs-18 lh-18 px-5 py-2 w-fit rounded-[10px] mb-3"
                      style={{color: '#FFFFFF', background: '#D92D20'}}
                    >
                      Ya no está disponible
                    </div>
                  )}
                  <div className="flex gap-[16px] relative">
                    <div className="w-[120px] h-[120px] object-cover rounded-[16px] flex-shrink-0">
                      <ImageWrapper
                        image={mainImage}
                        alt={product.label || product.name}
                        display="block"
                        width="100%"
                        height="100%"
                        backgroundColor="transparent"
                        margin="0"
                        padding="0"
                        borderRadius="30px"
                        transition={true}
                        loadDelay={0.1}
                        onLoad={undefined}
                      />
                    </div>

                    {/* Información del producto */}
                    <div className="flex-1 min-w-0">
                      {product.seller_name && (
                        <div
                          className="secondary-font-bold fs-16 px-5 py-2 w-fit rounded-[10px] mb-1 flex items-center gap-2"
                          style={{color: '#FFFFFF', background: sellerColor, textShadow: '#000000 2px 2px 2px'}}
                        >
                          <ImageWrapper
                            image={locationIcon}
                            alt="Location"
                            display="block"
                            width="24px"
                            height="24px"
                            backgroundColor="transparent"
                            margin="0"
                            padding="0"
                            borderRadius="0"
                            transition={true}
                            loadDelay={0.1}
                            onLoad={undefined}
                          />
                          <div className="overflow-hidden text-ellipsis line-clamp-1 pr-[3px]">
                            {product.seller_name}
                          </div>
                        </div>
                      )}
                      <h3 className="fs-29 lh-24 main-font-bold font-bold mb-1 text-detail-title pr-[40px]">
                        {product.label || product.name}
                      </h3>
                      <p className="secondary-font-regular fs-18 lh-18">
                        Unidad:
                        <span className="text-md text-subtotal-price"> {new Currency(currency, locale).format(commonPrice)}</span>
                        <span className="text-xs line-through"> {itemNormalSubtotal > itemCommonSubtotal && new Currency(currency, locale).format(itemNormalSubtotal)}</span>
                      </p>

                      {/* Editar modificadores. Va sobre la lista de opciones y con forma de enlace,
                          como en las apps de delivery: el py-2 mantiene un área táctil cómoda sin
                          dibujar una caja alrededor. */}
                      {productModifiers.length > 0 && (
                        <motion.button
                          onClick={() => handleEditItem(item, product)}
                          disabled={isUnavailable}
                          className={`w-fit mt-2 py-2 text-primary fs-18 lh-22 secondary-font-bold underline underline-offset-4 decoration-2 ${isUnavailable ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                          whileTap={isUnavailable ? {} : { scale: 0.95 }}
                        >
                          {extras.length > 0 ? 'Editar opciones' : 'Elegir opciones'}
                        </motion.button>
                      )}

                      {/* Lista de extras */}
                      {extras.length > 0 && (
                        <div className="space-y-0.5 mt-2">
                          {extras.map((extra, index) => (
                            <div key={index} className="text-(--color-text-subtle) fs-14 lh-18 main-font-regular">
                              <span>↳ {extra.name}</span>
                              <span className="text-bold">{extra.quantity && extra.quantity > 1 ? ` (x${extra.quantity})` : ""}</span>
                              <span className="text-md text-subtotal-price">{extra.price && extra.price > 0 ? ` ${new Currency(currency, locale).format(extra.price * (extra.quantity || 1))}` : ""}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Promociones aplicadas */}
                      {item.appliedPromotions && item.appliedPromotions.length > 0 && (
                        <div className="space-y-0.5 mt-2">
                          {item.appliedPromotions.map((promo, pIndex) => (
                            <div key={`promo-${pIndex}`} className="fs-14 lh-18 secondary-font-bold text-common-price flex items-center gap-1">
                              <span>★ {promo.name}</span>
                              <span className="font-bold">{promo.quantity && promo.quantity > 1 ? ` (x${promo.quantity})` : ""}</span>
                              <span> -{new Currency(currency, locale).format(promo.discount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Botón eliminar */}
                    <motion.button
                      onClick={() => handleRemove(item)}
                      className="cursor-pointer absolute top-0 right-0 w-[32px] h-[32px] flex items-center justify-center"
                      whileTap={{ scale: 0.8 }}
                      whileHover={{ scale: 1.5 }}
                    >
                      <Trash2 className="w-[24px] h-[24px] text-[#CD5C5C]" />
                    </motion.button>
                  </div>

                  {/* Controles de cantidad y totales */}
                  <div className="mt-[16px] flex items-center justify-between">
                    {/* Selector de cantidad */}
                    <div className="flex items-center gap-[37px]">
                      <motion.button
                        onClick={() => handleQuantityDecrease(item)}
                        className={`w-[59px] h-[59px] rounded-[15px] border-2 border-[#E8E8E8] flex items-center justify-center transition-colors ${isUnavailable ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                        whileTap={isUnavailable ? {} : { scale: 0.8 }}
                        whileHover={isUnavailable ? {} : { scale: 1.1 }}
                        disabled={item.quantity <= 1 || isUnavailable}
                      >
                        <Minus className="w-7 h-7" style={{ strokeWidth: 3 }} />
                      </motion.button>

                      <AnimatePresence mode="wait">
                        <motion.span
                          key={item.quantity}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                          className="fs-30 text-detail-title font-bold min-w-[40px] text-center"
                        >
                          {item.quantity.toString().padStart(2, '0')}
                        </motion.span>
                      </AnimatePresence>
                      <motion.button
                        onClick={() => handleQuantityIncrease(item)}
                        className={`w-[59px] h-[59px] rounded-[15px] border-2 border-[#E8E8E8] flex items-center justify-center transition-colors ${isUnavailable ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                        whileTap={isUnavailable ? {} : { scale: 0.8 }}
                        whileHover={isUnavailable ? {} : { scale: 1.1 }}
                        disabled={isUnavailable}
                      >
                        <Plus className="w-7 h-7" style={{ strokeWidth: 3 }} />
                      </motion.button>
                    </div>

                    {/* Subtotal y Total */}
                    <div className="flex gap-3 justify-end mx-4">
                      <div className="text-left">
                        <p className="text-subtotal-price fs-24">
                          Subtotal:
                        </p>
                      </div>
                      <div className="flex-auto text-right">
                        <AnimatePresence mode="wait">
                          <motion.p
                            key={itemNormalTotal}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                            className="text-subtotal-price fs-24 line-through"
                          >
                            {itemNormalTotal > itemCommonTotal && new Currency(currency, locale).format(itemNormalTotal)}
                          </motion.p>
                        </AnimatePresence>
                        <AnimatePresence mode="wait">
                          <motion.p
                            key={itemCommonTotal}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                            className="text-total-price fs-32 font-bold"
                          >
                            {new Currency(currency, locale).format(itemCommonTotal)}
                          </motion.p>
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            );
          })
        )}
      </div>

      {/* ESPACIADOR */}
      <div></div>

      {/* SECCIÓN RESUMEN FIJA AL FINAL */}
      <div className="max-w-screen-xl mx-auto fixed bottom-[200px] left-0 right-0 px-[40px] z-10">
        <div className="bg-back rounded-[20px] p-[30px] shadow-[0_2px_12px_rgba(0,0,0,0.1)]">
          {normalTotal > commonTotal ? (
            <div className="flex justify-between items-center mb-2">
              <span className="text-(--color-text-subtle) fs-24">
                Subtotal
              </span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={normalTotal}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-(--color-text-subtle) fs-24 line-through"
                >
                  {new Currency(currency, locale).format(normalTotal)}
                </motion.span>
              </AnimatePresence>
            </div>
          ) : null}
          {totalPromoDiscount > 0 && (
            <div className="flex justify-between items-center mb-4 text-common-price">
              <span className="fs-24 font-bold">Ahorro Promociones</span>
              <span className="fs-24 font-bold">-{new Currency(currency, locale).format(totalPromoDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-total-price fs-32 font-bold">
              Total
            </span>
            <AnimatePresence mode="wait">
              <motion.span
                key={commonTotal}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="text-total-price fs-32 font-bold"
              >
                {new Currency(currency, locale).format(commonTotal)}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* BOTONES INFERIORES FIJOS */}
      <div className="fixed bottom-0 left-0 right-0 bg-mid px-8 py-6 z-20 shadow-[0px_-1px_3px_0px_rgba(0,0,0,0.1),0px_-1px_2px_-1px_rgba(0,0,0,0.1)]">
        {/* El aviso va en su propia fila: dentro del slot del botón competía por el ancho y en
            pantallas de 720px terminaba desarmando la botonera. La fila replica la estructura de la
            botonera —mismo espaciador, mismo gap, mismo reparto— para que el aviso quede centrado
            sobre el botón Pagar y lo siga a cualquier resolución. */}
        {hasUnavailableItems ? (
          <div className="max-w-screen-xl mx-auto mb-3 flex items-center justify-between gap-4">
            <div className="w-[200px]" aria-hidden="true"></div>
            <motion.div
              animate={noticeShake}
              className="w-[650px] text-center secondary-font-bold fs-20 lh-24"
              style={{color: '#D92D20'}}
            >
              Quita los productos marcados
            </motion.div>
          </div>
        ) : null}
        <div className='max-w-screen-xl mx-auto flex items-center justify-between gap-4'>
          {/* Botón Volver */}
          <motion.button
            onClick={handleBack}
            className="cursor-pointer flex items-center justify-center gap-2 w-[200px] h-[120px] rounded-[25px] btn btn-blank transition-colors secondary-font-bold"
            whileTap={{ scale: 0.8 }}
            whileHover={{ scale: 1.1 }}
          >
            <ChevronLeft className="w-8 h-8 text-bold" />
            <span className="text-bold fs-28">Volver</span>
          </motion.button>

          {/* Botón Pagar */}
          {cartItems.length === 0 || hasUnavailableItems ? (
            <motion.div
              onClick={hasUnavailableItems ? shakeNotice : null}
              disabled={true}
              className={`opacity-50 w-[650px] h-[120px] rounded-[40px] btn btn-primary flex items-center justify-center shadow-lg ${hasUnavailableItems ? 'cursor-pointer' : ''}`}
              whileTap={hasUnavailableItems ? { scale: 0.97 } : {}}
            >
              <span className="a11y-pay-label text-5xl lh-38">Pagar <span className="a11y-pay-price">({new Currency(currency, locale).format(commonTotal)})</span></span>
            </motion.div>
          ) : null}

          {cartItems.length > 0 && !hasUnavailableItems ? (
            <motion.button
              onClick={handleCheckout}
              disabled={cartItems.length === 0}
              className="cursor-pointer w-[650px] h-[120px] rounded-[40px] btn btn-primary flex items-center justify-center shadow-lg disabled:opacity-50"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.03 }}
              animate={cartItems.length === 0 ? {} : { boxShadow: ['0px 0px 30px 0px var(--color-primary-glow)', '0px 0px 30px 15px var(--color-primary-glow)', '0px 0px 30px 0px var(--color-primary-glow)'] }}
              transition={cartItems.length === 0 ? {} : { boxShadow: { delay: 0, duration: 2, repeat: Infinity, ease: 'linear' } }}
            >
              <span className="a11y-pay-label text-5xl lh-38">Pagar <span className="a11y-pay-price">({new Currency(currency, locale).format(commonTotal)})</span></span>
            </motion.button>
          ) : null}
        </div>
      </div>

      {/* Edición de modificadores de un item del carro */}
      <ProductDetailModal
        currency={currency}
        locale={locale}
        storeId={storeId}
        product={editingProduct}
        validProducts={validProducts}
        modifiers={editingModifiers}
        sellers={sellers}
        sellerColors={sellerColors}
        open={!!editingItem && !!editingProduct}
        onClose={() => setEditingItem(null)}
        onUpsellClick={() => {}}
        onAddToCart={handleSaveItem}
        getProductById={getProductById}
        filteredStoreIds={filteredStoreIds}
        logActivity={logActivity}
        drawerContainer={drawerContainer}
        initialQuantity={editingItem?.quantity || 1}
        initialSelectedExtras={editingItem?.selectedExtras || {}}
        editMode={true}
      />
    </div>
  );
}
